import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  $accountCardListStore,
  $transferReasonsStore,
  $transferRequestsCreateStore,
  $transferRequestsFeeStore,
  $transferRequestsGetFxRateStore,
  $transferRequestsUpdateStore,
} from '@lib/store';
import { AmountUtils, cn, PaletteUtils, PaymentUtils, RoutePaths } from '@shared/utils';
import { type AccountResponse, AccountType, AmountTypeEnum, TransferRecipientTypeEnum, TransferRequestTypeEnum } from '@shared/api';
import { FilesFormBlock, FilesFormData, PageContainer, PaymentFormBlock, PaymentFormData, SelectAccount, TransferConfirmationBlock } from '@widgets';
import { Icon, Button, Text, Spinner } from '@shared/widgets';
import { Constants } from '@shared/constants';
import ArrowIcon from '@assets/icons/arrow.svg';
import ArrowVCFCIcon from '@assets/icons/arrow-vcfc.svg';

type SettlementCardFormData = { accountFrom?: AccountResponse; accountTo?: AccountResponse } & PaymentFormData & FilesFormData;
const DEFAULT_VALUES: SettlementCardFormData = {
  accountFrom: undefined,
  accountTo: undefined,
  amountType: AmountTypeEnum.BeneficiaryAmount,
  amount: undefined,
  beneficiaryAmount: undefined,
  paymentReason: '',
  purpose: '',
  files: [],
};

export const PaymentCardSettlementPage = () => {
  const accountStore = useUnit($accountCardListStore.store);
  const feeStore = useUnit($transferRequestsFeeStore.store);
  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);
  const transferRequestsUpdateStore = useUnit($transferRequestsUpdateStore.store);
  const transferReasonsStore = useUnit($transferReasonsStore.store);

  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);

  const { t } = useTranslation('payments');
  const formControl = useForm<SettlementCardFormData>({ defaultValues: DEFAULT_VALUES });
  const { control, formState, handleSubmit, watch, setValue } = formControl;
  const accountFrom = watch('accountFrom');
  const accountTo = watch('accountTo');
  const amount = watch('amount');
  const beneficiaryAmount = watch('beneficiaryAmount');

  useEffect(() => {
    Promise.allSettled([$accountCardListStore.events.getAccounts(), $transferReasonsStore.events.getTransferReasons()]);

    return () => {
      $accountCardListStore.events.clear();
      $transferRequestsGetFxRateStore.events.clear();
      $transferRequestsCreateStore.events.clear();
      $transferRequestsUpdateStore.events.clear();
    };
  }, []);

  useEffect(() => {
    if (accountStore.data.length > 0) setValue('accountFrom', accountStore.data[0]);
  }, [accountStore.data]);

  useEffect(() => {
    if (!accountFrom || !accountTo) return;
    if (accountFrom.currency === accountTo.currency) {
      $transferRequestsGetFxRateStore.events.setRate(1);
      return;
    }
    $transferRequestsGetFxRateStore.events.getFxRate({
      AccountId: accountFrom.id,
      FromCurrency: accountFrom.currency,
      ToCurrency: accountTo.currency,
      CountryCode: 'DE',
    });
  }, [accountTo?.currency, accountFrom?.currency]);

  const requestFeeHandler = useCallback(() => {
    if (!amount || !accountTo) return;
    $transferRequestsFeeStore.events.getFee({
      AccountId: accountFrom?.id,
      SenderAmount: amount,
      FastPayment: true,
      BeneficiaryCurrencyCode: accountTo?.currency,
      BeneficiaryBankCountryCode: 'US',
      TransferRequestType: TransferRequestTypeEnum.CardAccountSettlement,
    });
  }, [accountFrom?.id, accountTo?.currency, amount]);
  useEffect(() => {
    requestFeeHandler();
    return () => $transferRequestsFeeStore.events.clear();
  }, [accountFrom?.id, accountTo?.id]);

  const submitHandler = async (data: SettlementCardFormData) => {
    if (!data.accountFrom || !data.accountTo) return;

    if (transferRequestsCreateStore?.data) {
      await PaymentUtils.updateTransfer({
        account: data.accountFrom,
        accountTo: data.accountTo,
        payment: { ...data, id: transferRequestsCreateStore.data.id, type: TransferRequestTypeEnum.CardAccountSettlement },
      });
      if (transferRequestsCreateStore.data?.id && data.files?.length) {
        const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transferRequestsCreateStore.data.id, files: data.files });
        setValue('files', files);
      }
      setShowPaymentModal(true);
      return;
    }
    const transfer = await PaymentUtils.createTransfer({
      account: data.accountFrom,
      accountTo: data.accountTo,
      payment: { ...data, type: TransferRequestTypeEnum.CardAccountSettlement },
    });
    if (transfer.data?.id && data.files?.length) {
      const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transfer.data.id, files: data.files });
      setValue('files', files);
    }
    setShowPaymentModal(true);
  };

  const reasonOptions = useMemo(() => {
    const bankCountryCode = Constants.BANK_COUNTRY_CODE;
    const individualReasons = transferReasonsStore.data.filter((reason) => reason?.beneficiaryType?.includes(TransferRecipientTypeEnum.Business));
    const countryReasons = individualReasons.filter((reason) => reason.countryCode?.toUpperCase() == bankCountryCode);
    const reasons = countryReasons.length > 0 ? countryReasons : individualReasons.filter((reason) => !reason.countryCode);
    return reasons.map((reason) => ({ label: reason.name, value: reason.id }));
  }, [transferReasonsStore.data]);
  return (
    <PageContainer
      title={t('title')}
      header={
        <Link className='group flex items-center gap-2' to={RoutePaths.payments.onNavigate()}>
          <Icon
            icon={PaletteUtils.isInsigniaPalette ? <ArrowIcon /> : <ArrowVCFCIcon className='text-black dark:text-white' />}
            className={cn(PaletteUtils.isInsigniaPalette && 'rotate-180')}
          />
          {PaletteUtils.isInsigniaPalette ? (
            <Text weight='semibold' size='2xl'>
              {t('header')}
            </Text>
          ) : (
            <Text className='group-hover:underline' size='sm'>
              {t('return')}
            </Text>
          )}
        </Link>
      }
      return
    >
      <Spinner show={formState.isSubmitting} position />
      <FormProvider {...formControl}>
        <div
          className={cn(
            'flex flex-col gap-6',
            PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:border dark:border-gray-black dark:bg-soft-black',
          )}
        >
          <Text weight={PaletteUtils.getPaletteParam('semibold')} size='2xl'>
            {t('cardSettlement.title')}
          </Text>
          <form className='flex flex-col gap-6' noValidate onSubmit={handleSubmit(submitHandler)}>
            <Controller
              name='accountFrom'
              control={control}
              rules={{ required: t('errors.form.required', { ns: 'common' }) }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <SelectAccount
                  label={t('cardSettlement.placeholders.transferFrom')}
                  loading={accountStore.loading}
                  type={AccountType.Payment}
                  accounts={accountStore.data}
                  error={error?.message}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              name='accountTo'
              control={control}
              rules={{ required: t('errors.form.required', { ns: 'common' }) }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <SelectAccount
                  label={t('cardSettlement.placeholders.selectCardAccount')}
                  loading={accountStore.loading}
                  type={AccountType.Card}
                  accounts={accountStore.data.filter((account) => account.currency === 'EUR' && account.id !== accountFrom?.id)}
                  error={error?.message}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <PaymentFormBlock
              account={accountFrom}
              accountTo={accountTo}
              currency={accountFrom?.currency}
              reasonOptions={reasonOptions}
              onRequestFee={requestFeeHandler}
            />
            <FilesFormBlock transactionId={transferRequestsCreateStore.data?.id} />
            <Button
              className={cn('mt-3 max-w-72', PaletteUtils.isVcfcPalette && 'mt-0 w-[285px]')}
              fullWidth
              color='primary'
              type='submit'
              disabled={feeStore.loading || formState.isSubmitting || transferRequestsCreateStore.loading || transferRequestsUpdateStore.loading}
            >
              {t('form.buttons.transfer')} {AmountUtils.transformToAmountWithCurrency(beneficiaryAmount, accountTo?.currency)}
            </Button>
          </form>
        </div>
        {transferRequestsCreateStore.data?.id && (
          <TransferConfirmationBlock
            showPaymentModal={showPaymentModal}
            onClosePaymentModal={() => setShowPaymentModal(false)}
            account={accountFrom!}
            accountTo={accountTo}
          />
        )}
      </FormProvider>
    </PageContainer>
  );
};
